// cleanup_remove_external_libraries_GENERICS
// cleanup_convert_to_typescript_file_GENERICS

import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEqual } from 'lodash';
import { zip } from 'rxjs';
import { take } from 'rxjs/operators';

import { unsubscribeComponent } from 'utils/unsubscribeComponent';
import Spinner from 'components/spinner/Spinner';

import reducer, { ACTIONS } from './SpinResolver.reducer';
import useStyles from './SpinResolver.styles';

const subscriptions = {
  allServicesSub: null,
  reserviceSub: null
};

const SpinResolver = ({ children, className, services }) => {
  const { alignment, hidden, spinnerContainer } = useStyles();
  const servicesRef = useRef(services);
  const [data, dispatch] = useReducer(reducer, {
    loading: true,
    results: [],
    reloads: [],
    syncTimes: [],
    lastSync: null
  });

  const { lastSync, loading, reloads, results, syncTimes } = data;

  const fetchService = useCallback(
    (service, serviceIndex) => (props, isLoading) => {
      const service$ = service(props).pipe(take(1));
      dispatch({ type: ACTIONS.SET_LOADING, payload: Boolean(isLoading) });
      subscriptions.reserviceSub = service$.subscribe((newResult) => {
        dispatch({ type: ACTIONS.FETCH_SERVICE_SUCCESS, payload: { newResult, serviceIndex } });
      });
      return service$;
    },
    []
  );

  const fetchAllServices = useCallback(() => {
    subscriptions.allServicesSub = zip(...services.map((service) => service()))
      .pipe(take(1))
      .subscribe(
        (results) => {
          dispatch({
            type: ACTIONS.FETCH_ALL_SERVICES_SUCCESS,
            payload: { results, services, fetchService }
          });
        },
        // needs to be cancelled
        (err) => {
          dispatch({
            type: ACTIONS.FETCH_ALL_SERVICES_FAILURE,
            payload: err
          });
        }
      );
  }, [fetchService, services]);

  const refetchAllServices = useCallback(
    ({ loading }) => {
      dispatch({ type: ACTIONS.SET_LOADING, payload: Boolean(loading) });
      fetchAllServices();
    },
    [fetchAllServices]
  );

  const show = useCallback(() => dispatch({ type: ACTIONS.SET_LOADING, payload: true }), []);

  useEffect(() => {
    fetchAllServices();
    return () => unsubscribeComponent(subscriptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEqual(servicesRef.current, services)) {
      refetchAllServices({});
      servicesRef.current = services;
    }
  });

  return (
    <>
      <div
        className={classnames(spinnerContainer, { [hidden]: !loading }, className)}
        data-loading={loading}
      >
        <Spinner />
      </div>
      <div className={classnames(alignment, { [hidden]: loading }, className)}>
        {results.length
          ? children({
              lastSync,
              reload: () => refetchAllServices({ loading: true }),
              reloads,
              results,
              show,
              syncTimes
            })
          : null}
      </div>
    </>
  );
};

SpinResolver.propTypes = {
  className: PropTypes.string,
  children: PropTypes.func.isRequired,
  services: PropTypes.arrayOf(PropTypes.func).isRequired
};

SpinResolver.defaultProps = {
  className: ''
};

export default SpinResolver;
